/**
 * packages/ui/react/FileToMatter.css — RUN MA STEP 2.
 *
 * 🔴 EVERY COLOUR IS A TOKEN, AND THE GROUND DECIDES WHICH TOKEN — MEASURED, NOT CHOSEN BY EYE.
 * ops/run-ma/contrast.mjs parses the SHIPPED packages/ui/themes.css and composites every pair below
 * by hand up the ancestor chain, in all 19 themes, dark included. Its numbers govern this file.
 *
 * ⚠️ NO `color-mix()` AND NO rgba() GLASS ON ANY TEXT GROUND. Both are documented reader blind
 * spots: `color-mix()` parses as near-black in the measuring readers, and an `rgba(…, 0.1)` wash has
 * been read as an opaque wall. Every ground below is a flat, named token, so what the reader
 * composites is what the eye sees.
 *
 * ⭐ AND TEXT NEVER CARRIES MEANING BY COLOUR ALONE. The failure note is `--cc-ink` — the same ink
 * as body copy, so it clears 4.5:1 wherever body copy does — and the critical colour appears only on
 * a 2px rule, where the 3:1 non-text bar applies. A palette cannot make an error unreadable here.
 */

/* ⚠️ A COLUMN, SO THE NOTICE SITS UNDER THE CONTROL RATHER THAN BESIDE IT. The button is the only
   thing on the first row, so the wrapper still measures as a button wherever a host lays its action
   row out; the notice extends it downwards only when there is one. `align-items:flex-start` keeps
   the button its natural width instead of stretching it to the notice's. */
.cc-f2m { position: relative; display: inline-flex; flex-direction: column; align-items: flex-start; }

.cc-f2m__ic {
  width: 14px; height: 14px; flex: none;
  fill: none; stroke: currentColor; stroke-width: 2;
  stroke-linecap: round; stroke-linejoin: round;
}

/* ── THE MENU ──────────────────────────────────────────────────────────────────────────────────
   `--cc-card` is the card ground the ink tokens are derived AGAINST, which is why the menu is a
   card and not a translucent panel: it keeps every pair below on the pairing the theme solved. */
.cc-f2m__menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 70;
  min-width: 248px;
  max-width: 320px;
  max-height: 300px;
  overflow-y: auto;
  padding: 6px;
  background: var(--cc-card);
  /* 🔴 THE GROUND DECIDES THE BORDER TOKEN, AND RUN CF ALREADY SPLIT THE ROLE FOR THIS EXACT CASE.
     `--cc-edge` is `edgeFor(r)` — the seed reduced through toContrast(…, 3) against card, cardAlt,
     raised, hover AND the stage — so it clears 3:1 on the whole CARD family (worst 3.38:1, m3-yellow)
     and nowhere is it solved against the PAGE. `--cc-edge-on-page` is `toContrast(r.edge, r.page, 3)`.
     Measured, the two are NOT interchangeable: --cc-edge on the page falls to 1.05:1 (m8-magenta),
     and --cc-edge-on-page on the card falls to 1.00:1 (m4-coral, where it derives to the card colour
     itself). So the HOST sets the ground's token; the default is the card, which is Clark's.
     ⚠️ In Clark the menu's fill IS `--cc-card` on a `--cc-card` modal card — 1:1 — so this 1px
     border is not reinforcement, it is the only thing saying a control is there. */
  border: 1px solid var(--cc-f2m-edge, var(--cc-edge));
  border-radius: 10px;
  box-shadow: var(--cc-shadow-overlay);
  text-align: left;
}

/* The scope sentence. Recessive, but it is a SENTENCE A LAWYER MUST BE ABLE TO READ, so it takes
   the secondary ink the themes solve to the text bar — never a muted/disabled tone. */
.cc-f2m__scope {
  margin: 2px 4px 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--cc-hairline);
  font-size: 11.5px;
  line-height: 1.45;
  color: var(--cc-ink-secondary);
}

.cc-f2m__note {
  margin: 0;
  padding: 8px 10px;
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--cc-ink-secondary);
}

/* 🔴 FULL INK, NOT A RED TEXT COLOUR. See the header. The dot carries the alarm; the sentence
   carries the meaning, and it reads at body contrast in every theme. */
.cc-f2m__note--bad {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: var(--cc-ink);
}

/* The one place the critical colour appears. Non-text, so the 3:1 bar applies and it clears it in
   all 19 themes (worst 5.05:1, gavel). `flex:none` + the top margin keep it on the first LINE of a
   wrapping sentence instead of centring itself against the whole block. */
.cc-f2m__dot {
  flex: none;
  width: 7px;
  height: 7px;
  margin-top: 5px;
  border-radius: 50%;
  background: var(--cc-meter-critical);
}

.cc-f2m__opt {
  display: flex;
  align-items: baseline;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
  text-align: left;
  color: var(--cc-ink);
  background: transparent;
  border: none;
  border-radius: 7px;
  cursor: pointer;
}
.cc-f2m__opt:hover { background: var(--cc-card-hover); }
.cc-f2m__opt:focus-visible { outline: 2px solid var(--cc-focus-ring); outline-offset: -2px; }

.cc-f2m__optname { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }

/* "already filed" — secondary ink, because it is a fact the lawyer acts on, not decoration. */
.cc-f2m__optnote {
  flex: none;
  font-size: 11px;
  color: var(--cc-ink-secondary);
}

.cc-f2m__link {
  font: inherit;
  font-size: inherit;
  font-weight: 600;
  padding: 0;
  color: var(--cc-accent-text);
  background: none;
  border: none;
  text-decoration: underline;
  cursor: pointer;
}
.cc-f2m__link:focus-visible { outline: 2px solid var(--cc-focus-ring); outline-offset: 2px; }

/* ⭐ THE REFUSAL NOTICE — A CONTAINED CARD UNDER THE CONTROL (Adam, 2026-08-25).
   It wears the MENU's border, radius and ground on purpose: the two things this control can open
   are then visibly one family, and a server sentence long enough to wrap stays inside something
   instead of trailing off across the host's action row.
   ⚠️ It and the menu never coexist — opening the menu clears the error (onPrimary) — so the menu's
   `top: calc(100% + 6px)` can go on measuring the wrapper without this shifting it. */
.cc-f2m__err {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 10px;
  max-width: 320px;
  background: var(--cc-card);
  border: 1px solid var(--cc-f2m-edge, var(--cc-edge));
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.45;
  color: var(--cc-ink);
}
.cc-f2m__errtext { min-width: 0; overflow-wrap: anywhere; }

/* ⚠️ THE PHONE LAYOUTS. Clark and Research both have them (Draft does not — it blocks at 390 and
   that is documented, not fixed here). At 390 an absolutely-positioned 248px menu anchored to a
   button near the right edge can leave the viewport, so it pins to the left of its wrapper and is
   allowed to take the width it needs. */
@media (max-width: 480px) {
  .cc-f2m__menu {
    left: auto;
    right: 0;
    min-width: 0;
    width: min(280px, calc(100vw - 32px));
  }
  .cc-f2m__err { max-width: min(280px, calc(100vw - 32px)); }
}

/* ⚠️ THE PAGE-MOUNTED HOST. Research's OpinionPane sets no background of its own; `.st-page` is
   `var(--cc-page)`, so the menu floats on the page and takes the page's edge token.
   🔴 REPORTED, AND IT IS THE DESIGN SYSTEM'S CEILING RATHER THAN THIS CONTROL'S CHOICE: on
   m4-coral the boundary reaches only 2.80:1. `edgeOnPageFor` is `toContrast(r.edge, r.page, 3)`
   and toContrast cannot reach 3 against that coral page, so it returns the closest it can. The
   menu's own fill measures the same 2.80:1 there, so no border token available fixes it. This
   affects EVERY control mounted on m4-coral's page, not only this one; hand-picking a colour here
   would fork the palette to hide a system-level gap. 18 of 19 themes clear the bar. */
.st-opinion .cc-f2m,
.st-page .cc-f2m { --cc-f2m-edge: var(--cc-edge-on-page); }
